import { LoadingButton } from "@mui/lab";
import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	Stack,
	Switch,
	Typography,
} from "@mui/material";
import { useAccessibleUserGroupsLazyQuery, type UserGroup } from "@repo/server-api";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { useUserMutations } from "../hooks/useUserMutations";
import { emptyUser } from "../types/UserTypes";

import type { User } from "../types/UserTypes";

export type EditLoginDialogHandle = {
	show: (user: User) => void;
};

export type EditLoginDialogProps = {
	onSuccess?: () => void;
};

export const EditLoginDialog = forwardRef<EditLoginDialogHandle, EditLoginDialogProps>(
	({ onSuccess }, ref) => {
		const { t } = useTranslation();

		const [open, setOpen] = useState(false);
		const [user, setUser] = useState<User>(emptyUser);
		const [errors, setErrors] = useState<Record<string, string>>({});
		const [availableGroups, setAvailableGroups] = useState<UserGroup[]>([]);

		const { updateUser, isLoading } = useUserMutations();

		const [getAccessibleGroups] = useAccessibleUserGroupsLazyQuery({
			onCompleted: (data) => {
				if (data?.accessibleUserGroups) {
					const filteredGroups = data.accessibleUserGroups.filter((group) =>
						[
							"employee",
							"freelancer",
							"freelancer_of_supplier",
							"project_manager",
							"tenant_administrator",
							"tenant_hr",
							"contractor_administrator",
							"contractor_hr",
							"accountant",
						].includes(group.name),
					);
					setAvailableGroups(filteredGroups);
				}
			},
		});

		const validationSchema = Yup.object().shape({
			groups: Yup.array().min(1, t("validation.groups-required")),
		});

		useImperativeHandle(ref, () => ({
			show: (userToEdit: User) => {
				setUser(userToEdit);
				setErrors({});
				getAccessibleGroups();
				setOpen(true);
			},
		}));

		const handleClose = useCallback(() => {
			setOpen(false);
			setUser(emptyUser);
			setErrors({});
		}, []);

		const validate = useCallback(async (): Promise<boolean> => {
			try {
				await validationSchema.validate(user, { abortEarly: false });
				setErrors({});
				return true;
			} catch (err: unknown) {
				if (err instanceof Yup.ValidationError) {
					const newErrors: Record<string, string> = {};
					err.inner.forEach((error: Yup.ValidationError) => {
						if (error.path) {
							newErrors[error.path] = error.message;
						}
					});
					setErrors(newErrors);
				}
				return false;
			}
		}, [user, validationSchema]);

		// Convert group names to IDs for API calls
		const getGroupIds = useCallback(
			(groupNames: string[]): string[] => {
				return groupNames
					.map((name) => availableGroups.find((g) => g.name === name)?.id)
					.filter((id): id is string => id !== undefined);
			},
			[availableGroups],
		);

		const handleSubmit = useCallback(async () => {
			const isValid = await validate();
			if (!isValid) return;

			// Convert group names to IDs for API
			const groupIds = getGroupIds(user.groups);

			const success = await updateUser({ ...user, groups: groupIds });

			if (success) {
				handleClose();
				onSuccess?.();
			}
		}, [user, validate, updateUser, handleClose, onSuccess, getGroupIds]);

		const handleFieldChange = useCallback((field: keyof User, value: boolean) => {
			setUser((prev) => ({ ...prev, [field]: value }));
		}, []);

		// Toggle group selection using names (backend returns names)
		const handleGroupToggle = useCallback((groupName: string) => {
			setUser((prev) => {
				const isSelected = prev.groups.includes(groupName);
				const newGroups = isSelected
					? prev.groups.filter((g) => g !== groupName)
					: [...prev.groups, groupName];
				return { ...prev, groups: newGroups };
			});
			setErrors((prev) => ({ ...prev, groups: "" }));
		}, []);

		// Check if group is selected by name
		const isGroupSelected = (group: UserGroup) => user.groups.includes(group.name);

		return (
			<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
				<DialogTitle>{`${t("edit")}: ${user.username}`}</DialogTitle>
				<DialogContent>
					<Stack spacing={3} sx={{ mt: 1 }}>
						<Stack direction="row" alignItems="center" spacing={2}>
							<FormControlLabel
								control={
									<Switch
										checked={user.enabled}
										onChange={(e) => handleFieldChange("enabled", e.target.checked)}
									/>
								}
								label={t("login-enabled")}
							/>
							<Typography variant="body2" color="text.secondary">
								{user.emailVerified ? t("email-verified") : t("email-not-verified")}
							</Typography>
						</Stack>

						<Stack spacing={0.5}>
							<Typography variant="body2" color={errors.groups ? "error" : "text.secondary"}>
								{t("groups")}
							</Typography>
							<FormGroup>
								{availableGroups.map((group) => (
									<FormControlLabel
										key={group.id}
										control={
											<Checkbox
												checked={isGroupSelected(group)}
												onChange={() => handleGroupToggle(group.name)}
												size="small"
											/>
										}
										label={t(`groups.${group.name}`)}
									/>
								))}
							</FormGroup>
							{errors.groups && <FormHelperText error>{errors.groups}</FormHelperText>}
						</Stack>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button variant="outlined" onClick={handleClose}>
						{t("cancel")}
					</Button>
					<LoadingButton loading={isLoading} variant="contained" onClick={handleSubmit}>
						{t("save")}
					</LoadingButton>
				</DialogActions>
			</Dialog>
		);
	},
);

EditLoginDialog.displayName = "EditLoginDialog";
